import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants';

import type { TypeGetReturnOrdersParams, TypeGetReturnOrdersResponse, TypeReturnData } from '~/types/claim.types';
import type { TypeErrorResponseData } from '~/types/common.types';

/**
 * [반품 신청/조회] 반품 신청 리스트 조회 api 요청 훅
 */
const useGetReturnOrders = () => {
  const { runAsync } = useAsyncHandler();

  const getReturnOrders = useAPI<TypeGetReturnOrdersParams, TypeGetReturnOrdersResponse>({
    endpoint: API_ENDPOINT.CLAIM.GET_RETURN_ORDERS,
    method: 'get',
  });

  const requestReturnOrders = async (
    params: TypeGetReturnOrdersParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetReturnOrdersResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getReturnOrders({ params }), {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestReturnOrders };
};

export default useGetReturnOrders;

const RETURN_LIST_ROWS: [status: TypeReturnData['status'], trackingCount: number][] = [
  [{ code: 'RETURN_RECEIPT_COMPLETE', name: '회수예정' }, 0],
  [{ code: 'RETURN_DELIVERY_ING', name: '회수중' }, 1],
  [{ code: 'RETURN_INSPECTION_SUCCESS', name: '검수성공' }, 2],
  [{ code: 'RETURN_INSPECTION_FAILED', name: '검수실패' }, 1],
  [{ code: 'RETURN_DELIVERY_FAILED', name: '회수실패' }, 1],
];
const RETURN_MEMBER_NAMES = ['김컬리', '이물류', '박배송', '최반품', '정교환'];

const makeReturnOrder = (index: number): TypeReturnData => {
  const [status, trackingCount] = RETURN_LIST_ROWS[index - 1] ?? RETURN_LIST_ROWS[0];
  return {
    returnReceiptId: `RT20260519000${index}`,
    clientOrderCode: `CL-20260518-000${index}`,
    outboundOrderCode: `OB20260518000${index}`,
    returnCourier: { code: 'LOTTE', name: '롯데' },
    status,
    member: { name: RETURN_MEMBER_NAMES[index - 1] ?? '김컬리' },
    returnOrderItems:
      index === 1
        ? [
            { goodsCode: 'G00000001', goodsName: '[컬리] 유기농 바나나 1kg' },
            { goodsCode: 'G00000002', goodsName: '[컬리] 무항생제 특란 20구' },
          ]
        : [{ goodsCode: `G0000001${index}`, goodsName: `[컬리] 샘플 상품 ${index}` }],
    returnOrderTrackings: Array.from({ length: trackingCount }, (_, i) => ({ invoiceNumber: `25001234${index}${i}0` })),
    returnReceiptAt: `2026-05-19 08:1${index}:00`,
    updatedAt: `2026-05-19 08:5${index}:00`,
  };
};

const buildReturnOrdersResponse = (count: number): TypeGetReturnOrdersResponse => ({
  data: Array.from({ length: count }, (_, i) => makeReturnOrder(i + 1)),
  pagination: { listView: 50, listTotal: count, cursor: 0, totalCount: count },
});

registerMockAPI<TypeGetReturnOrdersResponse>({
  method: 'get',
  endpoint: '/api-oms/return-orders',
  label: '반품교환/반품신청조회/목록',
  scenarios: [
    { name: '정상 응답 — 5건', response: buildReturnOrdersResponse(5) },
    { name: '검색 결과 0건', response: buildReturnOrdersResponse(0) },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '반품 목록을 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
